import { useEffect, useLayoutEffect, useRef, type CSSProperties, type RefObject } from "react";

/**
 * The sliding tab pill, look "A · Metal ring" (owner, 2026-10-12).
 *
 * The chosen tab sits in a lit pewter ring around a pale fill. When the choice
 * changes the pill springs to the new tab, stretching along its path and
 * squashing across it while it moves, and a faint gold glint runs over the
 * ring once it lands. On a computer the whole bar swells from 0.94 to 1 as the
 * mouse comes near; touch never swells.
 *
 * Shared by the Discover rail (`@majourneys/discover`) and the account tabs
 * (`apps/majourneys`), so the spring is tuned in one place.
 *
 * How a surface uses it:
 * - The pill's resting place is plain CSS, written as a function of one custom
 *   property (say `transform: translateX(calc(var(--tab-i) * 100%))`). Server
 *   HTML therefore already has the pill on the right tab, before any script.
 * - `useSlidingPill` reads where that CSS puts the pill before and after a
 *   change and plays the spring between the two. It only ever writes
 *   `transform` (and the glint's `opacity`), never width, height or inset:
 *   layout animation stutters on iOS.
 * - The pill must not carry a CSS `transition` on transform: the spring is the
 *   motion, and a transition would fight it.
 */

/** The lit-metal ring (PewterFrame), 3px, around a pale fill. No drop shadow. */
export const METAL_RING_STYLE: CSSProperties = {
  border: "3px solid transparent",
  backgroundImage:
    "linear-gradient(180deg,#FFFFFF 0%,#F1F2F8 100%), linear-gradient(155deg,rgba(255,255,255,1) 0%,rgba(180,184,210,.55) 28%,rgba(17,14,57,.18) 55%,rgba(180,184,210,.55) 78%,rgba(255,255,255,1) 100%)",
  backgroundOrigin: "border-box, border-box",
  backgroundClip: "padding-box, border-box",
  boxShadow: "none",
};

/** The glint layer that crosses the ring once on landing. Starts hidden. */
export const PILL_GLINT_STYLE: CSSProperties = {
  position: "absolute",
  inset: 0,
  opacity: 0,
  pointerEvents: "none",
  background:
    "linear-gradient(100deg, transparent 20%, rgba(229,178,101,.35) 45%, rgba(255,255,255,.9) 50%, rgba(229,178,101,.35) 55%, transparent 80%)",
};

/** Label colours: navy 700 on the chosen tab, muted 600 on the rest. */
export const TAB_INK_ON = "#110E39";
export const TAB_INK_OFF = "#5a5d77";

export interface SpringSample {
  /** Progress, 0 to 1. */
  readonly x: number;
  /** Speed at this frame. */
  readonly v: number;
}

/** Samples a damped spring from 0 to 1 at 60fps, with its speed at each step. */
export function springSamples(stiffness: number, damping: number, frames: number): SpringSample[] {
  const out: { x: number; v: number }[] = [];
  let x = 0;
  let v = 0;
  const dt = 1 / 60;
  for (let i = 0; i < frames; i++) {
    for (let s = 0; s < 4; s++) {
      const a = stiffness * (1 - x) - damping * v;
      v += (a * dt) / 4;
      x += (v * dt) / 4;
    }
    out.push({ x, v });
  }
  out[out.length - 1] = { x: 1, v: 0 };
  return out;
}

export const PILL_SPRING = springSamples(260, 22, 32);
const PEAK_V = Math.max(...PILL_SPRING.map((s) => Math.abs(s.v)));
/** Most the pill stretches along its path, and squashes across it. */
export const PILL_STRETCH = 0.16;
export const PILL_SQUASH = 0.07;

/**
 * The keyframes from one resting place to another, in px. The pill stretches
 * along whichever axis it travels further on (X across a row, Y down a column).
 */
export function pillKeyframes(
  from: { x: number; y: number },
  to: { x: number; y: number },
): Keyframe[] {
  const alongY = Math.abs(to.y - from.y) > Math.abs(to.x - from.x);
  return PILL_SPRING.map((s) => {
    const k = Math.abs(s.v) / PEAK_V;
    const along = 1 + PILL_STRETCH * k;
    const across = 1 - PILL_SQUASH * k;
    const x = from.x + (to.x - from.x) * s.x;
    const y = from.y + (to.y - from.y) * s.x;
    const sx = alongY ? across : along;
    const sy = alongY ? along : across;
    return {
      transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px) scale(${sx.toFixed(4)}, ${sy.toFixed(4)})`,
    };
  });
}

export const PILL_SPRING_MS = Math.round((PILL_SPRING.length / 60) * 1000);

/** The translation part of an element's computed transform, in px. */
function readTranslate(el: Element): { x: number; y: number } | null {
  const t = getComputedStyle(el).transform;
  if (!t || t === "none") return { x: 0, y: 0 };
  if (typeof DOMMatrixReadOnly === "undefined") return null;
  try {
    const m = new DOMMatrixReadOnly(t);
    return { x: m.m41, y: m.m42 };
  } catch {
    return null;
  }
}

export function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

// Runs before paint on the client, and quietly does nothing on the server.
const useIsoLayoutEffect = typeof window === "undefined" ? useEffect : useLayoutEffect;

export interface UseSlidingPillOptions {
  /** The chosen tab. A negative index means none; the pill is not animated. */
  readonly index: number;
  /** The pill element. Its CSS transform must read `var(indexVar)`. */
  readonly pillRef: RefObject<HTMLElement | null>;
  /** Optional glint layer inside the pill (style it with PILL_GLINT_STYLE). */
  readonly glintRef?: RefObject<HTMLElement | null>;
  /** The custom property the pill's CSS transform is written in. */
  readonly indexVar: string;
  /**
   * Set when choosing a tab remounts the bar (each Discover section is its
   * own route, so the rail is a new element a moment after the click). A
   * spring still running when the old bar goes is picked up by the new one,
   * at the same point in its run, instead of the pill snapping home.
   */
  readonly handoffKey?: string;
}

interface Flight {
  readonly from: { x: number; y: number };
  readonly to: { x: number; y: number };
  readonly index: number;
  readonly startedAt: number;
}

/** Springs in flight, by handoff key: read by the bar that mounts next. */
const flights = new Map<string, Flight>();

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function play(
  pill: HTMLElement,
  glint: HTMLElement | null | undefined,
  from: { x: number; y: number },
  to: { x: number; y: number },
  elapsed: number,
): void {
  const run = pill.animate(pillKeyframes(from, to), { duration: PILL_SPRING_MS, easing: "linear" });
  if (elapsed > 0) run.currentTime = elapsed;
  if (glint && typeof glint.animate === "function") {
    glint.getAnimations().forEach((a) => a.cancel());
    const shine = glint.animate(
      [
        { transform: "translateX(-120%)", opacity: 0 },
        { transform: "translateX(-40%)", opacity: 1, offset: 0.35 },
        { transform: "translateX(120%)", opacity: 0 },
      ],
      { duration: 700, delay: 180, easing: "cubic-bezier(.22,1,.36,1)", fill: "both" },
    );
    if (elapsed > 0) shine.currentTime = elapsed;
  }
}

/**
 * Plays the spring whenever `index` changes. The pill's resting transform
 * stays CSS; this only animates between the old resting place and the new
 * one, so an interrupted move, a resize or an RTL page all land where the CSS
 * says. With prefers-reduced-motion the pill simply jumps.
 */
export function useSlidingPill({
  index,
  pillRef,
  glintRef,
  indexVar,
  handoffKey,
}: UseSlidingPillOptions): void {
  const prev = useRef<number | null>(null);

  useIsoLayoutEffect(() => {
    const was = prev.current;
    prev.current = index;
    const pill = pillRef.current;
    if (!pill || index < 0) return;
    if (typeof pill.animate !== "function" || prefersReducedMotion()) return;

    // First render of this bar: only a spring handed over by the bar it
    // replaced moves it. A fresh page load never animates.
    if (was === null) {
      const flight = handoffKey ? flights.get(handoffKey) : undefined;
      if (!flight || flight.index !== index) return;
      const elapsed = now() - flight.startedAt;
      if (elapsed >= PILL_SPRING_MS) return;
      const to = readTranslate(pill);
      if (!to) return;
      play(pill, glintRef?.current, flight.from, to, elapsed);
      return;
    }
    if (was === index || was < 0) return;

    // Where the pill is now: mid-flight if a spring is still running, else
    // where the old index put it (pinned on the pill itself for one read).
    const running = pill.getAnimations();
    let from: { x: number; y: number } | null;
    if (running.length > 0) {
      from = readTranslate(pill);
      running.forEach((a) => a.cancel());
    } else {
      pill.style.setProperty(indexVar, String(was));
      from = readTranslate(pill);
      pill.style.removeProperty(indexVar);
    }
    const to = readTranslate(pill);
    if (!from || !to) return;
    if (from.x === to.x && from.y === to.y) return;

    play(pill, glintRef?.current, from, to, 0);
    if (handoffKey) flights.set(handoffKey, { from, to, index, startedAt: now() });
  }, [index, indexVar, pillRef, glintRef, handoffKey]);
}

export interface UseProximitySwellOptions {
  /** Scale while the mouse is far away. */
  readonly rest?: number;
  /** How near, in px, the mouse must come before the bar starts to swell. */
  readonly radius?: number;
  readonly enabled?: boolean;
}

/**
 * The bar swells from `rest` to 1 as a mouse comes within `radius` px. Only a
 * fine hovering pointer counts: touch never swells (a pressed tab squeezes
 * instead, in the surface's CSS). Writes the individual `scale` property, so
 * it stacks on top of any `transform` the bar already has (the desktop rail is
 * centred with translateY). Nothing changes until the first mouse move, so a
 * page never shrinks on load, and nothing moves with reduced motion.
 */
export function useProximitySwell(
  ref: RefObject<HTMLElement | null>,
  { rest = 0.94, radius = 140, enabled = true }: UseProximitySwellOptions = {},
): void {
  useEffect(() => {
    const el = ref.current;
    if (!el || !enabled || typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    let frame = 0;
    let last: PointerEvent | null = null;
    const apply = () => {
      frame = 0;
      const e = last;
      if (!e) return;
      const r = el.getBoundingClientRect();
      if (r.width === 0 && r.height === 0) return; // hidden copy of the bar
      const dx = Math.max(r.left - e.clientX, 0, e.clientX - r.right);
      const dy = Math.max(r.top - e.clientY, 0, e.clientY - r.bottom);
      const near = 1 - Math.min(Math.hypot(dx, dy) / radius, 1);
      el.style.scale = (rest + (1 - rest) * near).toFixed(3);
    };
    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || !fine.matches || reduced.matches) {
        if (el.style.scale) el.style.scale = "";
        return;
      }
      last = e;
      if (!frame) frame = requestAnimationFrame(apply);
    };
    window.addEventListener("pointermove", onMove, { passive: true });
    return () => {
      window.removeEventListener("pointermove", onMove);
      if (frame) cancelAnimationFrame(frame);
      el.style.scale = "";
    };
  }, [ref, rest, radius, enabled]);
}

/** The transition the swelling bar wears, so the scale eases between moves. */
export const SWELL_TRANSITION = "scale 220ms cubic-bezier(.22,1,.36,1)";
